/* eslint-disable no-param-reassign */
const void_tags = require('./void_tags').index
const Node = require('./node_type_constants')
const _ = require('../underscore')
// const log = require('log').create('html token sanitizer') // eslint-disable-line

// token stringifier
//
// converts an array of tokens matching the DOM NodeList spec into an html string
// e.g.
// [{
//     "nodeType": 1,
//     "tagName": "p",
//     "attributes": [],
//     "childNodes": [{
//         "nodeType": 3,
//         "data": "A single paragraph."
//     }]
// }]
//     becomes =>
// <p>A single paragraph.</p>
//
// This stringifier is agnostic by default. Pass in options below to disallow
// certain tags, or to unwrap others.
//
// e.g. '<span>foo</span>' => 'foo')


const defaults = {
    max_length: Infinity,
    exclude_empty_tags: false,
    allowed_tags: null,
    disallowed_tags: null,
    unwrapped_tags: null,
    allowed_attributes: null,
    disallowed_attributes: null,
    required_attributes: null,
    massageTextContent(txt) {
        return txt
    },
    massageElementContent(txt) {
        return txt
    },
}

// TokenStringifier class
// using a class so we can track bio length on each instance
class TokenStringifier {

    constructor(tokens, options = {}) {
        this.options = _.defaults(options, defaults)
        if (
            this.options.allowed_tags
            && this.options.unwrapped_tags
        ) {
            _.extend(this.options.allowed_tags, this.options.unwrapped_tags)
        }
        this.length = 0
        this.html = this.stringifyTokens(tokens)

    }

    stringifyTokens(tokens) {
        return (
            tokens
            .map(token => this.stringifyToken(token))
            .join('')
        )
    }

    stringifyToken(token) {
        if (this.length >= this.options.max_length) return ''
        switch (token.nodeType) {
            case Node.TEXT_NODE:
                return this.stringifyTextToken(token)
            default:
                return this.stringifyTagToken(token)
        }
    }

    stringifyTextToken(token) {
        const remainder = this.options.max_length - this.length
        const txt = (
            this.options.massageTextContent(token.data)
            .slice(0, remainder)
        )

        this.length += txt.length
        return txt
    }

    stringifyTagToken(token) {
        const tagName = token.tagName

        // if there's an allowed tag list,
        // return empty for tags not in the allowed tag list
        if (
            this.options.allowed_tags
            && ! this.options.allowed_tags[tagName]
        ) {
            return ''
        }

        // if there's a disallowed tag list,
        // return empty for tags in the disallowed tag list
        if (
            this.options.disallowed_tags
            && this.options.disallowed_tags[tagName]
        ) {
            return ''
        }


        if (void_tags[tagName]) {
            return this.stringifyVoidTagToken(token)
        }

        return this.stringifyWrapperTagToken(token)
    }

    stringifyVoidTagToken(token) {
        const tagName = token.tagName
        const tagNameLower = tagName.toLowerCase()
        const attributes = this.stringifyAttributes(token)
        return `<${tagNameLower}${attributes}>`
    }

    stringifyWrapperTagToken(token) {
        const tagName = token.tagName

        const content = this.options.massageElementContent(
            this.stringifyTokens(token.childNodes)
        )

        if (
            ! content
            && this.options.exclude_empty_tags
        ) {
            return ''
        }

        if (
            this.options.unwrapped_tags
            && this.options.unwrapped_tags[tagName]
        ) {
            return content
        }

        const tagNameLower = tagName.toLowerCase()
        const attributes = this.stringifyAttributes(token)
        return `<${tagNameLower}${attributes}>${content}</${tagNameLower}>`
    }

    stringifyAttributes(token) {
        const attrs = this.filterAttributes(token)
        const dict = this.convertAttributesArrayToDict(attrs)

        if (this.options.required_attributes) {
            const required = this.options.required_attributes[token.tagName] || {}
            _.extend(dict, required)
        }

        const str = (
            Object.keys(dict)
            // TODO need to figure out how to support quotes inside attributes
            .map(key => ` ${key}="${this.stripQuotes(dict[key])}"`)
            .join('')
        )
        return str
    }

    filterAttributes(token) {
        let attrs = token.attributes

        if (this.options.allowed_attributes) {
            const allowed = this.options.allowed_attributes[token.tagName]
            if (allowed) {
                attrs = attrs.filter(attr => allowed[attr.name])
            }
        }
        if (this.options.disallowed_attributes) {
            const disallowed = this.options.disallowed_attributes[token.tagName]
            if (disallowed) {
                attrs = attrs.filter(attr => disallowed[attr.name])
            }
        }

        return attrs
    }

    convertAttributesArrayToDict(attrs) {
        const dict = {}
        attrs.forEach(attr => {
            dict[attr.name] = attr.value
        })
        return dict
    }

    stripQuotes(str) {
        return str.replace(/"/g, '')
    }
}

// helper function which creates a new TokenStringifier and returns the stringified html
function stringify(tokens, options) {
    const token_stringifier = new TokenStringifier(tokens, options)
    return token_stringifier.html
}

module.exports = stringify
